/**
 * MatcapShaderMaterial - custom shader material with matcap texturing and reveal effects.
 * Supports matcap-based lighting, ground clipping, and animated reveal transitions.
 *
 * **WebGPU:** `ShaderMaterial` does not run on the WebGPU backend. `GenesysWebGPURenderer`
 * converts this material to {@link GenesysMatcapNodeMaterial} through the renderer node library
 * (`library.fromMaterial`) using `getOrCreateWebGpuMatcapFromSource` in `matcapDualBackend.ts`.
 * Assets and `ResourceManager.loadMaterial` stay as this class.
 */

import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { ISerializableObjectTag } from '../../utils/serialization/serializable.js';

import type { IDumper, ILoader } from '../../utils/serialization/factories.js';

export interface MatcapParams {
  matcap: THREE.Texture;
  uRevealProgress?: number;
  uEnableReveal?: number;
  uIndirectDistanceAmplitude?: number;
  uIndirectDistanceStrength?: number;
  uIndirectDistancePower?: number;
  uIndirectAngleStrength?: number;
  uIndirectAngleOffset?: number;
  uIndirectAnglePower?: number;
  uIndirectColor?: THREE.Color | string | number;
  uClipBelowGround?: number;
  uGroundY?: number;
  name?: string;
}

const vertexShader = `
#define MATCAP
varying vec3 vViewPosition;
#ifndef FLAT_SHADED
  varying vec3 vNormal;
#endif
#include <common>
#include <uv_pars_vertex>
#include <displacementmap_pars_vertex>
#include <fog_pars_vertex>
#include <morphtarget_pars_vertex>
#include <skinning_pars_vertex>
#include <logdepthbuf_pars_vertex>
#include <clipping_planes_pars_vertex>

uniform float uRevealProgress;
uniform float uEnableReveal;
varying vec3 vWorldPosition;

void main() {
  #include <uv_vertex>
  #include <beginnormal_vertex>
  #include <morphnormal_vertex>
  #include <skinbase_vertex>
  #include <skinnormal_vertex>
  #include <defaultnormal_vertex>
  #ifndef FLAT_SHADED
    vNormal = normalize( transformedNormal );
  #endif
  #include <begin_vertex>
  #include <morphtarget_vertex>
  #include <skinning_vertex>
  #include <displacementmap_vertex>

  vec4 worldPosition = modelMatrix * vec4(transformed, 1.0);

  // Reveal (Y-up)
  if(uEnableReveal > 0.5) {
    float distanceToCenter = length(worldPosition.xyz);
    float yAmplitude = 3.2;
    float revealProgress = (uRevealProgress - distanceToCenter / 30.0) * 5.0;
    revealProgress = 1.0 - clamp(revealProgress, -0.1, 1.0);
    revealProgress = pow(revealProgress, 2.0);
    if(uRevealProgress > 0.99) { revealProgress = 0.0; }
    worldPosition.y -= revealProgress * yAmplitude;
  }

  vWorldPosition = worldPosition.xyz;
  vec4 mvPosition = viewMatrix * worldPosition;
  gl_Position = projectionMatrix * mvPosition;

  #include <logdepthbuf_vertex>
  #include <clipping_planes_vertex>
  #include <fog_vertex>

  vViewPosition = - mvPosition.xyz;
}
`;

const fragmentShader = `
#define MATCAP
uniform vec3 diffuse;
uniform float opacity;
uniform sampler2D matcap;
varying vec3 vViewPosition;
#ifndef FLAT_SHADED
  varying vec3 vNormal;
#endif
#include <common>
#include <uv_pars_fragment>
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <fog_pars_fragment>
#include <bumpmap_pars_fragment>
#include <normalmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

uniform float uIndirectDistanceAmplitude;
uniform float uIndirectDistanceStrength;
uniform float uIndirectDistancePower;
uniform float uIndirectAngleStrength;
uniform float uIndirectAngleOffset;
uniform float uIndirectAnglePower;
uniform vec3 uIndirectColor;
uniform float uClipBelowGround;
uniform float uGroundY;
varying vec3 vWorldPosition;

void main() {
  // Optional discard below ground (Y-up)
  if(uClipBelowGround > 0.5 && vWorldPosition.y < uGroundY) { discard; }
  #include <clipping_planes_fragment>

  vec4 diffuseColor = vec4(diffuse, opacity);
  #include <logdepthbuf_fragment>
  #include <map_fragment>
  #include <alphamap_fragment>
  #include <alphatest_fragment>
  #include <normal_fragment_begin>
  #include <normal_fragment_maps>

  vec3 viewDir = normalize( vViewPosition );
  vec3 x = normalize( vec3( viewDir.z, 0.0, - viewDir.x ) );
  vec3 y = cross( viewDir, x );
  vec2 uv = vec2( dot( x, normal ), dot( y, normal ) ) * 0.495 + 0.5;

  vec4 matcapColor = texture2D( matcap, uv );
  vec3 outgoingLight = diffuseColor.rgb * matcapColor.rgb;

  // Indirect/bounce light: distance above ground (uGroundY) and angle toward -Y
  float distAboveGround = vWorldPosition.y - uGroundY;
  float indirectDistanceStrength = clamp(1.0 - distAboveGround / uIndirectDistanceAmplitude, 0.0, 1.0) * uIndirectDistanceStrength;
  indirectDistanceStrength = pow(indirectDistanceStrength, uIndirectDistancePower);
  indirectDistanceStrength = clamp(indirectDistanceStrength, 0.0, 1.0);

  vec3 worldNormal = inverseTransformDirection(vNormal, viewMatrix);
  float indirectAngleStrength = dot(normalize(worldNormal), vec3(0.0, -1.0, 0.0)) + uIndirectAngleOffset;
  indirectAngleStrength = clamp(indirectAngleStrength * uIndirectAngleStrength, 0.0, 1.0);
  indirectAngleStrength = pow(indirectAngleStrength, uIndirectAnglePower);

  float indirectStrength = indirectDistanceStrength * indirectAngleStrength;

  gl_FragColor = vec4(mix(outgoingLight, uIndirectColor, indirectStrength), diffuseColor.a);
  #include <opaque_fragment>
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  #include <fog_fragment>
  #include <premultiplied_alpha_fragment>
  #include <dithering_fragment>
}
`;

@EngineClass('MatcapShaderMaterial')
export class MatcapShaderMaterial extends THREE.ShaderMaterial {
  readonly [ISerializableObjectTag] = true;

  /**
   * Diffuse tint. Shares its instance with `uniforms.diffuse.value` so WebGL keeps using the
   * uniform, and Three's TSL `materialColor` reference (used on the WebGPU backend) finds it
   * on the ShaderMaterial directly.
   */
  color!: THREE.Color;

  /**
   * Matcap texture. Shares its instance with `uniforms.matcap.value` for the same reason as
   * {@link color}: WebGPU's `MeshMatcapNodeMaterial` looks up `material.matcap` at build time
   * and at runtime via `materialReference('matcap', 'texture')`.
   */
  matcap!: THREE.Texture;

  constructor(params: MatcapParams) {
    // `UniformsLib.*` are shared singletons; clone before mutating so each material
    // instance has its own `diffuse`/`opacity` uniforms and can be tinted independently.
    const uniforms = {
      ...THREE.UniformsUtils.clone(THREE.UniformsLib.common),
      ...THREE.UniformsUtils.clone(THREE.UniformsLib.bumpmap),
      ...THREE.UniformsUtils.clone(THREE.UniformsLib.normalmap),
      ...THREE.UniformsUtils.clone(THREE.UniformsLib.displacementmap),
      ...THREE.UniformsUtils.clone(THREE.UniformsLib.fog),
      matcap: { value: params.matcap },
      uRevealProgress: { value: params.uRevealProgress ?? 1.0 },
      uEnableReveal: { value: params.uEnableReveal ?? 0.0 },
      uIndirectDistanceAmplitude: { value: params.uIndirectDistanceAmplitude ?? 1.75 },
      uIndirectDistanceStrength: { value: params.uIndirectDistanceStrength ?? 0.5 },
      uIndirectDistancePower: { value: params.uIndirectDistancePower ?? 2.0 },
      uIndirectAngleStrength: { value: params.uIndirectAngleStrength ?? 1.5 },
      uIndirectAngleOffset: { value: params.uIndirectAngleOffset ?? 0.6 },
      uIndirectAnglePower: { value: params.uIndirectAnglePower ?? 1.0 },
      uIndirectColor: { value: new THREE.Color(params.uIndirectColor ?? '#d04500') },
      uClipBelowGround: { value: params.uClipBelowGround ?? 0.0 },
      uGroundY: { value: params.uGroundY ?? 0.0 }
    } as Record<string, THREE.IUniform>;

    super({
      name: params.name ?? 'MatcapShaderMaterial',
      uniforms,
      defines: { MATCAP: '' },
      lights: false,
      transparent: false,
      vertexShader,
      fragmentShader,
    });

    // Matcap textures are usually authored in sRGB and not flipped
    if (params.matcap) {
      params.matcap.colorSpace = THREE.SRGBColorSpace;
      params.matcap.flipY = false;
    }

    // Mirror the shader uniforms onto the material so the WebGPU node pipeline
    // (which reads `material.color` and `material.matcap` at build/render time)
    // sees the same values as the WebGL uniforms. Share the exact instance so
    // mutating one updates the other automatically.
    this.color = uniforms.diffuse!.value as THREE.Color;
    this.matcap = uniforms.matcap!.value as THREE.Texture;
  }

  public serialize(dumper: IDumper): void {
    if (!dumper.dumpingAsPrefab) {
      dumper.dumpValue('$uuid', this.uuid);
    }
    dumper.dumpValue('name', this.name);
    dumper.dumpValue('matcap', this.uniforms.matcap?.value);
    dumper.dumpValue('uRevealProgress', this.uniforms.uRevealProgress?.value);
    dumper.dumpValue('uEnableReveal', this.uniforms.uEnableReveal?.value);
    dumper.dumpValue('uIndirectDistanceAmplitude', this.uniforms.uIndirectDistanceAmplitude?.value);
    dumper.dumpValue('uIndirectDistanceStrength', this.uniforms.uIndirectDistanceStrength?.value);
    dumper.dumpValue('uIndirectDistancePower', this.uniforms.uIndirectDistancePower?.value);
    dumper.dumpValue('uIndirectAngleStrength', this.uniforms.uIndirectAngleStrength?.value);
    dumper.dumpValue('uIndirectAngleOffset', this.uniforms.uIndirectAngleOffset?.value);
    dumper.dumpValue('uIndirectAnglePower', this.uniforms.uIndirectAnglePower?.value);
    dumper.dumpValue('uIndirectColor', (this.uniforms.uIndirectColor?.value as THREE.Color)?.getHexString ? `#${(this.uniforms.uIndirectColor.value as THREE.Color).getHexString()}` : this.uniforms.uIndirectColor?.value);
    dumper.dumpValue('uClipBelowGround', this.uniforms.uClipBelowGround?.value);
    dumper.dumpValue('uGroundY', this.uniforms.uGroundY?.value);
    // don't call `dumper.dumpProperties(this);` to dump the values from THREE.ShaderMaterial, as we handle construction by staticDeserialize now
  }

  public static staticDeserialize(data: any, loader: ILoader): MatcapShaderMaterial {
    const params: MatcapParams = {
      name: data.name,
      matcap: loader.loadValue('matcap'),
      uRevealProgress: loader.loadValue('uRevealProgress'),
      uEnableReveal: loader.loadValue('uEnableReveal'),
      uIndirectDistanceAmplitude: loader.loadValue('uIndirectDistanceAmplitude'),
      uIndirectDistanceStrength: loader.loadValue('uIndirectDistanceStrength'),
      uIndirectDistancePower: loader.loadValue('uIndirectDistancePower'),
      uIndirectAngleStrength: loader.loadValue('uIndirectAngleStrength'),
      uIndirectAngleOffset: loader.loadValue('uIndirectAngleOffset'),
      uIndirectAnglePower: loader.loadValue('uIndirectAnglePower'),
      uIndirectColor: loader.loadValue('uIndirectColor'),
      uClipBelowGround: loader.loadValue('uClipBelowGround'),
      uGroundY: loader.loadValue('uGroundY'),
    };
    const mat = new MatcapShaderMaterial(params);
    const uuid = loader.loadValue('$uuid');
    if (uuid) {
      (mat as any).uuid = uuid;
    }
    return mat;
  }
}
